<template>
  <div class="cashier-modal-overlay" @click.self="$emit('close')">
    <div class="cashier-modal" @click.stop>
      <div class="cashier-modal__header">
        <h5>
          {{ title }}
          <small v-if="editingItem" class="text-muted ml-2">
            ({{ editingItem.full_name }})
          </small>
        </h5>
        <button type="button" class="close-btn" @click="$emit('close')">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="cashier-modal__body">
        <div class="mb-3">
          <div class="d-flex align-items-center mb-2">
            <span class="mr-2">{{ $t('pluginCashiers.mode_label') }}:</span>
            <span class="badge" :class="mode === 'department' ? 'badge-primary' : 'badge-secondary'">
              {{ mode === 'department' ? $t('pluginCashiers.department') : $t('pluginCashiers.sender') }}
            </span>
          </div>
        </div>

        <div v-if="loading" class="text-center text-muted py-4">
          <i class="fa fa-spinner fa-spin mr-1"></i>
          {{ mode === 'department' ? $t('pluginCashiers.loading_departments') : $t('pluginCashiers.loading_counterparties') }}
        </div>

        <template v-else>
          <v-select
            v-model="localSelected"
            :options="safeOptions"
            :reduce="(i) => (i && typeof i === 'object' ? i.id : i)"
            :label="mode === 'department' ? 'title' : 'name'"
            multiple
            :placeholder="mode === 'department' ? $t('pluginCashiers.select_department_ph') : $t('pluginCashiers.select_counterparties_ph')"
            class="counterparties-select mb-3"
          >
            <template #option="{ name, title }">
              <span>{{ mode === 'department' ? title : name }}</span>
            </template>
            <template #selected-option="{ name, title }">
              <span>{{ mode === 'department' ? title : name }}</span>
            </template>
            <template #no-options>
              <span>{{ mode === 'department' ? $t('pluginCashiers.no_departments') : $t('pluginCashiers.no_counterparties') }}</span>
            </template>
          </v-select>

          <div v-if="localSelected.length > 0" class="selected-counterparties">
            <small class="text-muted">{{ $t('pluginCashiers.selected') }}: {{ localSelected.length }}</small>
          </div>
        </template>
      </div>
      <div class="cashier-modal__footer">
        <b-button variant="secondary" size="sm" @click="$emit('close')" class="mr-2">
          {{ $t('popup.cancel') }}
        </b-button>
        <b-button variant="primary" size="sm" @click="$emit('save')" :disabled="saving">
          <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>
          {{ $t('common.save') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CashierCounterpartiesModal',
  props: {
    title: {
      type: String,
      default: '',
    },
    editingItem: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'sender',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localSelected: this.normalizeSelection(this.selected),
    };
  },
  computed: {
    safeOptions() {
      return Array.isArray(this.options) ? this.options.filter(Boolean) : [];
    },
  },
  watch: {
    selected: {
      immediate: true,
      handler(val) {
        const next = this.normalizeSelection(val);
        if (this.selectionsEqual(next, this.localSelected)) {
          return;
        }
        this.localSelected = next;
      },
    },
    localSelected: {
      deep: true,
      handler(val) {
        if (this.selectionsEqual(val, this.selected)) {
          return;
        }
        this.$emit('update:selected', this.normalizeSelection(val));
      },
    },
  },
  methods: {
    normalizeSelection(val) {
      if (!Array.isArray(val)) return [];
      return val
        .map(item => (item && typeof item === 'object' ? item.id : item))
        .filter(item => item !== null && item !== undefined);
    },
    selectionsEqual(a, b) {
      const aa = this.normalizeSelection(a);
      const bb = this.normalizeSelection(b);
      if (aa.length !== bb.length) return false;
      for (let i = 0; i < aa.length; i += 1) {
        if (String(aa[i]) !== String(bb[i])) return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.cashier-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
}

.cashier-modal {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;

    h5 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }

    .close-btn {
      border: none;
      background: none;
      font-size: 18px;
      color: #6c757d;
      cursor: pointer;
      padding: 0;
      line-height: 1;

      &:hover {
        color: #343a40;
      }
    }
  }

  &__body {
    padding: 16px;
    overflow-y: auto;
    max-height: calc(90vh - 120px);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    background: #f8f9fa;
  }
}

.counterparties-select {
  ::v-deep .vs__dropdown-toggle {
    min-height: 32px;
  }

  ::v-deep .vs__selected {
    background: #e9ecef;
    border: none;
    font-size: 12px;
    padding: 2px 6px;
    margin: 2px;
  }

  ::v-deep .vs__deselect {
    margin-left: 4px;
  }
}
</style>
